/**
 * Datacom AI — Client chat unificato.
 *
 * IMPORTANTE: la chiamata reale al motore RAG passa SEMPRE da una edge function
 * server-side (`chat`), che a sua volta inoltra la richiesta a Dify.
 * DIFY_API_KEY non deve mai essere esposta nel frontend, in localStorage o nei log.
 * Mantenere questa regola anche in evoluzioni future del codice.
 */
import { supabase } from "@/integrations/supabase/client";
import { intestazioniPiattaforma } from "@/lib/dispositivo";
import { mockChat } from "./mockChat";
import { invitiAllAcquistoNellApp, TESTI_IOS } from "./iscrizione";
import type { ChatRequest, ChatResponse } from "./types";

let lastDegradedAt = 0;
let connectionStatus: ConnectionStatus = "unknown";

/** Eventi per il banner UI di fallback degraded. */
const DEGRADED_EVENT = "datacom:degraded";
const STATUS_EVENT = "datacom:chat-status";

/** Errore che il chiamante puo' riconoscere per mostrare l'invito al codice. */
/**
 * La prova dei 7 giorni (o l'abbonamento) e' finita: il server ha chiuso la
 * porta con un 403. Non e' un guasto ne' una quota: e' IL momento commerciale
 * dell'app, quello in cui si chiede lo sblocco a Datacom. Il chiamante lo
 * distingue per mostrare il popup, non un messaggio d'errore.
 */
export class ProvaScaduta extends Error {
  constructor(
    messaggio: string,
    /** true = era un abbonamento (dal 07/09/2026): il popup dice «rinnova», non «prova finita». */
    readonly rinnovo: boolean = false,
    readonly scadutoIl: string | null = null,
  ) {
    super(messaggio);
    this.name = "ProvaScaduta";
  }
}

/**
 * Il server non riconosce chi sta chiedendo.
 *
 * Da quando la prova e' quella dei sette giorni (26/08/2026) l'assistente non
 * risponde piu' a chi non ha un account. Nell'app non dovrebbe succedere - la
 * chat sta dietro l'accesso - ma una sessione puo' scadere proprio mentre si
 * scrive una domanda: in quel caso serve dirlo, non lasciare un errore muto.
 */
export class ServeAccount extends Error {
  constructor(messaggio: string) {
    super(messaggio);
    this.name = "ServeAccount";
  }
}

/**
 * Il profilo non ha la partita IVA: il server (dal 05/09/2026) non risponde
 * finche' non c'e'. Il chiamante porta al modulo che la chiede, come fa gia'
 * la guardia delle rotte quando se ne accorge da sola.
 */
export class ProfiloIncompleto extends Error {
  constructor(messaggio: string) {
    super(messaggio);
    this.name = "ProfiloIncompleto";
  }
}

/**
 * La partita IVA del profilo non risulta valida o attiva al Registro Imprese:
 * il server (dal 07/09/2026) chiude la porta. NON e' un guasto passeggero, e
 * dirlo cosi' e' peggio che tacere: fino all'08/09 questo caso non era gestito
 * qui, il corpo della risposta veniva consumato e si finiva nel ramo «motore
 * non raggiungibile» — l'installatore leggeva «problema temporaneo, riprova»
 * per sempre. Il chiamante porta al profilo, dove il numero si corregge.
 */
export class PartitaIvaNonValida extends Error {
  constructor(messaggio: string) {
    super(messaggio);
    this.name = "PartitaIvaNonValida";
  }
}

/**
 * Il server non e' riuscito a verificare l'account (profilo, account o
 * contatore non letti) e ha tenuto la porta CHIUSA invece di aprirla. Non e'
 * il motore che manca: e' un guasto passeggero nostro, e va detto cosi'.
 */
export class ControlloNonRiuscito extends Error {
  constructor(messaggio: string) {
    super(messaggio);
    this.name = "ControlloNonRiuscito";
  }
}

export class QuotaEsaurita extends Error {
  constructor(
    readonly usate: number,
    readonly limite: number,
    messaggio: string,
  ) {
    super(messaggio);
    this.name = "QuotaEsaurita";
  }
}

/**
 * Il motore non ha risposto. Si LANCIA, non si maschera.
 *
 * Prima, ogni fallimento ripiegava sul simulatore demo: l'utente riceveva
 * risposte preconfezionate che sembravano vere, con il solo banner (chiudibile)
 * a dirlo. Il 3 agosto un guasto CORS ha mandato in loop Umberto su frasi
 * finte del mock. Una risposta inventata che sembra vera e' peggio di un
 * errore: il simulatore resta solo per la modalita' demo scelta apposta.
 */
export class MotoreNonRaggiungibile extends Error {
  constructor(motivo: string) {
    super("Il motore di risposta non e' raggiungibile: " + motivo);
    this.name = "MotoreNonRaggiungibile";
  }
}

/**
 * IL NUMERO DEL DISPOSITIVO E' STATO ROTTAMATO (26/08/2026).
 *
 * Serviva a contare le domande di chi usava l'assistente senza account. Da
 * quando la prova sono i sette giorni, un account c'e' sempre e il conto si
 * tiene su quello. Un identificativo casuale che non serve piu' a niente resta
 * comunque un dato raccolto: da dichiarare nella privacy e da spiegare a chi
 * chiede. Quindi non si manda piu' e si CANCELLA quello gia' salvato sui
 * telefoni, senza aspettare che l'utente svuoti i dati del sito.
 */
try {
  localStorage.removeItem("datacom.device_id");
} catch {
  // Incognito o storage negato: non c'era niente da cancellare.
}

export type ConnectionStatus = "unknown" | "live" | "degraded";

function setStatus(next: ConnectionStatus) {
  connectionStatus = next;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(STATUS_EVENT, { detail: next }));
  }
}

export function getConnectionStatus(): ConnectionStatus {
  return connectionStatus;
}

export function onConnectionStatus(
  handler: (s: ConnectionStatus) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const fn = (e: Event) => handler((e as CustomEvent).detail as ConnectionStatus);
  window.addEventListener(STATUS_EVENT, fn);
  return () => window.removeEventListener(STATUS_EVENT, fn);
}

function notifyDegraded() {
  lastDegradedAt = Date.now();
  setStatus("degraded");
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(DEGRADED_EVENT));
  }
}

export function onDegraded(handler: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(DEGRADED_EVENT, handler);
  return () => window.removeEventListener(DEGRADED_EVENT, handler);
}

export function lastDegradedTimestamp() {
  return lastDegradedAt;
}

export interface SendOptions {
  /** Forza modalità mock. */
  demoMode?: boolean;
  /** Allegato immagine (singolo) da inviare come multipart/form-data. */
  image?: File | null;
  /** Chiamato a ogni token ricevuto dallo stream SSE. */
  onToken?: (chunk: string) => void;
  /** Chiamato all'evento meta finale (message_id, conversation_id, status, ...). */
  onMeta?: (meta: {
    message_id: string | null;
    conversation_id: string | null;
    status: import("./types").ChatStatus;
    source?: string;
    citations?: import("./types").Citation[];
    transparency?: import("./types").TransparencyMeta | null;
  }) => void;
  /** AbortSignal opzionale per cancellare lo stream. */
  signal?: AbortSignal;
}

export async function sendChatMessage(
  req: ChatRequest,
  opts: SendOptions = {},
): Promise<ChatResponse> {
  if (opts.demoMode) {
    const res = await mockChat(req);
    // Simula streaming token-per-token anche in demo, per UX coerente.
    if (opts.onToken && res.answer) {
      const words = res.answer.split(/(\s+)/);
      for (const w of words) {
        opts.onToken(w);
        await new Promise((r) => setTimeout(r, 18));
      }
    }
    opts.onMeta?.({
      message_id: res.messageId ?? null,
      conversation_id: res.difyConversationId ?? null,
      status: res.status,
      source: res.source,
      citations: res.citations,
    });
    return res;
  }

  // Streaming SSE diretto verso l'edge function `chat`.
  //
  // IL GUARDIANO DELL'INATTIVITA'. Il motore puo' metterci venti secondi buoni
  // a rispondere, ed e' normale: cerca in oltre duemila manuali. Quello che non e'
  // normale e' che ammutolisca e tenga la connessione aperta - succede quando
  // Dify si inceppa o la rete del cantiere se ne va a meta' frase. Senza
  // questo, l'utente resta a fissare "Ricerca nei manuali..." per sempre,
  // senza nemmeno un modo per annullare. Il timer si azzera a ogni pezzo di
  // risposta che arriva: conta il SILENZIO, non la durata.
  const SILENZIO_MAX_MS = 45_000;
  const controllore = new AbortController();
  const fermaTutto = () => controllore.abort();
  opts.signal?.addEventListener("abort", fermaTutto, { once: true });
  let scaduto = false;
  let vigile: ReturnType<typeof setTimeout> | undefined;
  const riarma = () => {
    if (vigile) clearTimeout(vigile);
    vigile = setTimeout(() => {
      scaduto = true;
      controllore.abort();
    }, SILENZIO_MAX_MS);
  };
  const smonta = () => {
    if (vigile) clearTimeout(vigile);
    opts.signal?.removeEventListener("abort", fermaTutto);
  };
  riarma();

  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
  const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
  const url = `${SUPABASE_URL}/functions/v1/chat`;

  let response: Response;
  try {
    // IL TOKEN DELL'UTENTE, NON LA CHIAVE PUBBLICA.
    // Fino al 20 agosto qui viaggiava sempre la publishable key: per il server
    // CHIUNQUE era anonimo - admin compreso - e chi era loggato veniva contato
    // come un dispositivo da tre domande al giorno. Il bug c'era dal 1 agosto,
    // e l'ha trovato Davide bloccato sul suo stesso account amministratore.
    // Con il token vero il server riconosce l'utente e applica il SUO stato:
    // prova, attivo, o scaduto.
    let tokenUtente = "";
    try {
      const { data } = await supabase.auth.getSession();
      tokenUtente = data.session?.access_token ?? "";
    } catch {
      tokenUtente = "";
    }
    const baseHeaders: Record<string, string> = {
      Authorization: `Bearer ${tokenUtente || SUPABASE_KEY}`,
      apikey: SUPABASE_KEY,
      Accept: "text/event-stream",
      // iPhone: il server applica l'account gratuito (solo norme, 3 a settimana).
      ...intestazioniPiattaforma(),
    };
    let reqInit: RequestInit;
    if (opts.image) {
      const fd = new FormData();
      fd.append("message", req.message);
      fd.append("selectedKnowledgeFilter", req.selectedKnowledgeFilter);
      fd.append("conversationId", req.conversationId);
      fd.append("difyConversationId", req.difyConversationId ?? "");
      fd.append("brands", JSON.stringify(req.brands ?? []));
      fd.append("image", opts.image, opts.image.name || "image.jpg");
      // Niente Content-Type: lo imposta il browser con boundary.
      reqInit = { method: "POST", headers: baseHeaders, body: fd, signal: controllore.signal };
    } else {
      reqInit = {
        method: "POST",
        headers: { ...baseHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          message: req.message,
          selectedKnowledgeFilter: req.selectedKnowledgeFilter,
          conversationId: req.conversationId,
          difyConversationId: req.difyConversationId ?? "",
          brands: req.brands ?? [],
        }),
        signal: controllore.signal,
      };
    }
    response = await fetch(url, reqInit);
  } catch (err) {
    smonta();
    if (scaduto) {
      notifyDegraded();
      throw new MotoreNonRaggiungibile("il motore non ha risposto in tempo");
    }
    // L'annullamento e' una scelta dell'utente, non un guasto.
    if ((err as Error)?.name === "AbortError") throw err;
    console.warn("[chat] fetch failed");
    notifyDegraded();
    throw new MotoreNonRaggiungibile("richiesta bloccata o rete assente");
  }

  const contentType = response.headers.get("content-type") ?? "";

  // Manca l'account. Va intercettato PRIMA del fallback demo: senza questo
  // controllo chi non poteva entrare riceveva una risposta finta
  // dal simulatore e non scopriva mai di dover inserire il codice Datacom. Il
  // momento in cui si chiede il codice e' l'unico che l'app ha per spiegarsi:
  // sprecarlo con una risposta inventata li perde entrambi.
  if (response.status === 401) {
    try {
      const d = await response.json();
      if (d?.error === "serve_account") {
        setStatus("live");
        throw new ServeAccount(
          invitiAllAcquistoNellApp()
            ? String(d.messaggio ?? "Per usare l'assistente serve un account: la valutazione dura sette giorni.")
            : "Per usare l'assistente serve un account: registra la tua impresa, è un attimo.",
        );
      }
    } catch (e) {
      if (e instanceof ServeAccount) throw e;
    }
  }

  if (response.status === 403) {
    try {
      const d = await response.json();
      if (d?.error === "prova_scaduta" || d?.error === "abbonamento_scaduto") {
        setStatus("live");
        // Sull'iPhone il testo del server («invia la richiesta a Datacom»)
        // non si mostra: e' un invito a comprare fuori (Apple 3.1.1, 29/09).
        throw new ProvaScaduta(
          !invitiAllAcquistoNellApp()
            ? d.error === "abbonamento_scaduto"
              ? TESTI_IOS.accessoNonAttivo
              : TESTI_IOS.valutazioneFinita
            : String(
                d.messaggio ??
                  (d.error === "abbonamento_scaduto"
                    ? "Il tuo accesso e' scaduto. Contatta Datacom per la riattivazione."
                    : "Il periodo di valutazione e' terminato. Contatta Datacom per l'account installatore."),
              ),
          d.error === "abbonamento_scaduto",
          typeof d.scaduto_il === "string" ? d.scaduto_il : null,
        );
      }
      if (d?.error === "profilo_incompleto") {
        setStatus("live");
        throw new ProfiloIncompleto(
          String(d.messaggio ?? "Per usare l'assistente serve completare il profilo con la partita IVA."),
        );
      }
      if (d?.error === "piva_non_valida") {
        setStatus("live");
        throw new PartitaIvaNonValida(
          String(
            d.messaggio ??
              "La partita IVA nel tuo profilo non risulta valida o attiva. Correggila dal profilo, oppure scrivi a Datacom.",
          ),
        );
      }
    } catch (e) {
      if (e instanceof ProvaScaduta || e instanceof ProfiloIncompleto || e instanceof PartitaIvaNonValida) throw e;
    }
  }

  // Il server non ha potuto verificare l'account e ha chiuso la porta (dal
  // 05/09/2026): non e' il motore, e non deve accendere il banner del motore.
  if (response.status === 503) {
    try {
      const d = await response.json();
      if (d?.error === "controllo_non_riuscito") {
        setStatus("live");
        throw new ControlloNonRiuscito(
          String(d.messaggio ?? "Non riesco a verificare il tuo account in questo momento. Riprova fra un minuto."),
        );
      }
    } catch (e) {
      if (e instanceof ControlloNonRiuscito) throw e;
    }
  }

  if (response.status === 429) {
    try {
      const d = await response.json();
      if (d?.error === "quota_esaurita") {
        setStatus("live");
        // Sull'iPhone niente «contattate Datacom per account aggiuntivi».
        const inValutazione = /prova|valutazione/i.test(String(d.messaggio ?? ""));
        throw new QuotaEsaurita(
          Number(d.usate ?? 0),
          Number(d.limite ?? 100),
          invitiAllAcquistoNellApp()
            ? String(d.messaggio ?? "Hai finito le domande a tua disposizione.")
            : inValutazione
              ? TESTI_IOS.quotaValutazione
              : TESTI_IOS.quotaSettimana,
        );
      }
    } catch (e) {
      if (e instanceof QuotaEsaurita) throw e;
    }
  }

  // Risposta JSON (degraded, errore upstream pre-stream) → errore onesto.
  if (!contentType.includes("text/event-stream")) {
    try {
      const d = await response.json();
      if (!d || d.degraded || d.status === "degraded" || d.error || !d.answer) {
        notifyDegraded();
        throw new MotoreNonRaggiungibile("risposta di errore dal servizio");
      }
      setStatus("live");
      // JSON di risposta legacy: simula token per coerenza UX.
      if (opts.onToken && typeof d.answer === "string") {
        opts.onToken(d.answer);
      }
      opts.onMeta?.({
        message_id: d.messageId ?? null,
        conversation_id: d.difyConversationId ?? null,
        status: d.status,
        source: d.source,
        citations: d.citations,
      });
      smonta();
      return d as ChatResponse;
    } catch (e) {
      if (e instanceof MotoreNonRaggiungibile) throw e;
      notifyDegraded();
      throw new MotoreNonRaggiungibile("risposta illeggibile dal servizio");
    }
  }

  // Streaming SSE.
  if (!response.body) {
    notifyDegraded();
    throw new MotoreNonRaggiungibile("flusso di risposta assente");
  }

  try {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let accumulated = "";
    let meta: {
      message_id: string | null;
      conversation_id: string | null;
      status: import("./types").ChatStatus;
      source?: string;
      citations?: import("./types").Citation[];
      transparency?: import("./types").TransparencyMeta | null;
      suggestions?: string[];
    } | null = null;
    let streamError: { error: string; code: string } | null = null;

    const processEvent = (raw: string) => {
      let eventType = "message";
      let dataStr = "";
      for (const line of raw.split("\n")) {
        if (line.startsWith("event:")) eventType = line.slice(6).trim();
        else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
      }
      if (!dataStr) return;
      let payload: Record<string, unknown>;
      try {
        payload = JSON.parse(dataStr) as Record<string, unknown>;
      } catch {
        return; // riga non valida: si ignora e si prosegue col resto del flusso
      }

      if (eventType === "token") {
        const text = typeof payload.text === "string" ? payload.text : "";
        if (text) {
          accumulated += text;
          opts.onToken?.(text);
        }
      } else if (eventType === "meta") {
        meta = {
          message_id: (payload.message_id as string | null) ?? null,
          conversation_id: (payload.conversation_id as string | null) ?? null,
          status: (payload.status as import("./types").ChatStatus) ?? "ok",
          source: payload.source as string | undefined,
          citations: payload.citations as import("./types").Citation[] | undefined,
          transparency: (payload.transparency as import("./types").TransparencyMeta) ?? null,
          // Senza questa riga i chip di disambiguazione non compaiono mai: il
          // motore li produce e la edge function li manda, ma qui andavano persi.
          suggestions: payload.suggestions as string[] | undefined,
        };
      } else if (eventType === "error") {
        streamError = {
          error: (payload.error as string) ?? "stream_error",
          code: (payload.code as string) ?? "upstream_error",
        };
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      riarma(); // e' arrivato qualcosa: il motore sta lavorando
      buffer += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        if (raw.trim()) processEvent(raw);
      }
    }
    if (buffer.trim()) processEvent(buffer);
    smonta();

    if (streamError) {
      console.warn("[chat] stream error", streamError);
      notifyDegraded();
      throw new MotoreNonRaggiungibile("errore durante lo stream");
    }

    if (!accumulated.trim()) {
      notifyDegraded();
      throw new MotoreNonRaggiungibile("risposta vuota dal motore");
    }

    setStatus("live");
    opts.onMeta?.(meta ?? {
      message_id: null,
      conversation_id: req.difyConversationId ?? null,
      status: "ok",
    });

    return {
      answer: accumulated,
      status: meta?.status ?? "ok",
      source: meta?.source,
      citations: meta?.citations,
      // Varianti fra cui scegliere, quando la domanda era ambigua.
      suggestions: meta?.suggestions,
      messageId: meta?.message_id ?? undefined,
      difyConversationId: meta?.conversation_id ?? null,
    };
  } catch (err) {
    smonta();
    if (err instanceof MotoreNonRaggiungibile) throw err;
    if (scaduto) {
      notifyDegraded();
      throw new MotoreNonRaggiungibile("il motore si e' interrotto a meta' risposta");
    }
    if ((err as Error)?.name === "AbortError") throw err;
    console.warn("[chat] stream unexpected error");
    notifyDegraded();
    throw new MotoreNonRaggiungibile("interruzione imprevista dello stream");
  }
}

// Il vecchio runDemoFallback non esiste piu': il simulatore risponde SOLO nella
// modalita' demo scelta dall'utente (ramo in cima a sendChatMessage). Su guasto
// si lancia MotoreNonRaggiungibile — mai una risposta finta che sembra vera.